import React, { useState } from 'react';
import * as Popover from '@radix-ui/react-popover';

interface LimitedItemsListProps<T> {
    items: T[];
    renderItem: (item: T, index: number) => React.ReactNode;
    renderPopoverItem: (item: T, index: number) => React.ReactNode;
    maxVisibleItems?: number; // Default: 3
    className?: string;
}

function LimitedItemsListComponent<T>({
    items,
    renderItem,
    renderPopoverItem,
    maxVisibleItems = 3,
    className = '',
}: LimitedItemsListProps<T>) {
    const [showPopover, setShowPopover] = useState(false);

    const visibleItems = items.slice(0, maxVisibleItems);
    const hiddenCount = Math.max(0, items.length - maxVisibleItems);
    const hasOverflow = hiddenCount > 0;

    const handleContainerClick = (e: React.MouseEvent) => {
        // If clicking on the container (not the item), toggle popover
        if (hasOverflow && e.target === e.currentTarget) {
            setShowPopover(!showPopover);
        }
    };

    const content = (
        <div
            className={`flex items-center ${className} w-full px-1 min-h-[26px] rounded ${
                hasOverflow ? 'cursor-pointer' : ''
            }`}
            onClick={handleContainerClick}
        >
            <div className="flex items-center min-w-0 flex-1">{visibleItems.map(renderItem)}</div>
            {hasOverflow && (
                <span
                    className="text-xs text-saga-gray-500 whitespace-nowrap flex-shrink-0 px-2 py-1 rounded hover:bg-saga-gray-250 dark:hover:bg-saga-gray-700 transition-colors ml-px cursor-pointer"
                    onClick={(e) => {
                        e.stopPropagation();
                        setShowPopover(!showPopover);
                    }}
                    role="button"
                    tabIndex={0}
                    aria-label={`Show ${hiddenCount} more items`}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            e.stopPropagation();
                            setShowPopover(!showPopover);
                        }
                    }}
                >
                    +{hiddenCount}
                </span>
            )}
        </div>
    );

    if (!hasOverflow) {
        return content;
    }

    return (
        <Popover.Root open={showPopover} onOpenChange={setShowPopover}>
            <Popover.Trigger asChild>
                <div>{content}</div>
            </Popover.Trigger>
            <Popover.Portal>
                <Popover.Content
                    sideOffset={-2}
                    align="start"
                    className="w-auto p-1 rounded shadow-popupSmall bg-white dark:bg-zinc-800 border border-saga-gray-200 dark:border-zinc-700 z-50"
                    role="dialog"
                    aria-label="All references"
                >
                    <div className="min-w-[200px] max-w-[300px] max-h-[368px] overflow-y-auto">
                        <div className="flex flex-col space-y-1">{items.map(renderPopoverItem)}</div>
                    </div>
                </Popover.Content>
            </Popover.Portal>
        </Popover.Root>
    );
}

// Export memoized version to prevent unnecessary re-renders in virtualized tables
export const LimitedItemsList = React.memo(LimitedItemsListComponent) as typeof LimitedItemsListComponent;
